import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { FeedstockService } from '../../feedstock/feedstock.service';
import { FinancialCapexService } from '../../financial/financial-capex/financial-capex.service';
import { FinancialOpexService } from '../../financial/financial-opex/financial-opex.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { ConfirmationDialogComponent } from '../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { EditFeedstockFinancialDialogComponent } from './edit-feedstock-financial-dialog/edit-feedstock-financial-dialog.component';
import { EditGeneratedProductFinancialDialogComponent } from './edit-generated-product-financial-dialog/edit-generated-product-financial-dialog.component';
import { AddCapexDialogComponent } from './add-capex-dialog/add-capex-dialog.component';
import { AddOpexDialogComponent } from './add-opex-dialog/add-opex-dialog.component';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import { FinancialCapex } from '../../../models/project/financial/financial-capex.model';
import { FinancialOpex } from '../../../models/project/financial/financial-opex.model';
import { ProductFeeType, Product, ProductType } from '../../../models/project/plant-specifications/product.model';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import Utils from '../../../util';
import { FinancialFeeType } from '../../../models/project/financial/financial-fee-type.enum';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-financial-input',
  templateUrl: './financial-input.component.html',
  styleUrls: [
    './financial-input.component.scss',
  ]
})
export class FinancialInputComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Output() pageDirty = new EventEmitter();

  trigger: string;

  onDestroyNotifier = new EventEmitter();

  generatedProducts: Product[] = [];
  feedstocks: Feedstock[] = [];

  capex: FinancialCapex[] = [];
  opex: FinancialOpex[] = [];

  totalCapex = new UnitValue();
  totalOpex = new UnitValue();

  showCapexEstimationConfirmation = false;
  showOpexEstimationConfirmation = false;

  opexPercentage: number;

  constructor(
    private dialog: MatDialog,
    private unitService: UnitService,
    private projectCommService: ProjectCommunicationService,
    private feedstockService: FeedstockService,
    private capexService: FinancialCapexService,
    private opexService: FinancialOpexService,
    private translationService: AnessaTranslationService,
  ) {
    super();
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.generatedProducts = this.projectCommService.getGeneratedProductItems();
          this.feedstocks = this.feedstockService.getFeedstocks();
        }
      });

    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.generatedProducts = this.projectCommService.getGeneratedProductItems();
          this.feedstocks = this.feedstockService.getFeedstocks();
        }
      });

    this.feedstockService.feedstockServiceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.feedstocks = this.feedstockService.getFeedstocks();
        }
      });

    this.feedstockService.feedstocksSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.feedstocks = this.feedstockService.getFeedstocks();
        }
      });

    this.capexService.capexLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.setupCapex();
        }
      });

    this.capexService.capexUpdatedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(updated => {
        if (updated) {
          this.setupCapex();
        }
      });

    this.opexService.opexLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.setupOpex();
        }
      });

    this.opexService.opexUpdatedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(updated => {
        if (updated) {
          this.setupOpex();
        }
      });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.setupCapex();
          this.setupOpex();
        }
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  private setupCapex() {
    if (!this.unitService.unitsLoadedNotifier.value || !this.capexService.capexLoadedNotifier.value) {
      return;
    }
    this.capex = this.capexService.getCapex();
    this.totalCapex = this.capexService.getTotalCapexInTargetCur();
    this.totalCapex.value = Utils.roundToPrecision(this.totalCapex.value, 2);
  }

  private setupOpex() {
    if (!this.unitService.unitsLoadedNotifier.value || !this.opexService.opexLoadedNotifier.value) {
      return;
    }
    this.opex = this.opexService.getOpex();
    this.totalOpex = this.opexService.getTotalOpexInTargetCur();
    this.totalOpex.value = Utils.roundToPrecision(this.totalOpex.value, 2);
    this.opexPercentage = this.opexService.getOpexPercentage();
  }

  isProjectCreated() {
    return this.projectCommService.getProjectId() !== '';
  }

  // Feedstock Financials

  getNormalizedPriceFeedstock(feedstock: Feedstock) {
    const price = feedstock.financial.price.value;
    const mass = feedstock.financial.amount.value;
    const value = Utils.roundToPrecision((price / mass), 2);
    return Utils.addCommas(value);
  }

  getNormalizedUnitFeedstock(feedstock: Feedstock) {
    const price = this.unitService.getUnitById(feedstock.financial.price.unit);
    const mass = this.unitService.getUnitById(feedstock.financial.amount.unit);

    if (!price) {
      return null;
    }

    return price.translationId + '/' + mass.translationId;
  }

  getFeedstockPricingArray() {
    const result = [];
    for (const feedstock of this.feedstocks) {
      if (feedstock.financial.feeType === FinancialFeeType.FREE) {
        result.push(this.translationService.get('FEE_TYPE.free'));
      } else if (feedstock.financial.amount.unit && Utils.isNumeric(feedstock.financial.amount.value)
        && feedstock.financial.price.unit && Utils.isNumeric(feedstock.financial.price.value)) {
        if (feedstock.financial.feeType === FinancialFeeType.TIPPING_FEE) {
          result.push(this.translationService.get('FEE_TYPE.tipping_fee') + ' ' +
            this.getNormalizedPriceFeedstock(feedstock) + ' ' + this.getNormalizedUnitFeedstock(feedstock));
        } else {
          result.push(this.translationService.get('FEE_TYPE.cost') + ' ' +
            this.getNormalizedPriceFeedstock(feedstock) + ' ' + this.getNormalizedUnitFeedstock(feedstock));
        }
      } else {
        result.push(null);
      }
    }
    return result;
  }

  onEditFeedstock(index: number) {
    const dialogRef = this.dialog.open(EditFeedstockFinancialDialogComponent, {
      width: '930px',
      height: '750px',
      disableClose: true,
      data: {
        feedstock: Feedstock.deepCopy(this.feedstockService.getFeedstock(index)),
      },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((feedstock: Feedstock) => {
        if (feedstock) {
          this.feedstockService.saveFeedstockFinancial(feedstock, index);
          this.pageDirty.emit(true);
        }
      });
  }

  // Generated Products Financials

  getNormalizedPriceProduct(product: Product) {
    const price = product.price.value;
    const mass = product.quantity.value;
    const value = Utils.roundToPrecision((price / mass), 2);
    return Utils.addCommas(value);
  }

  getNormalizedUnitProduct(product: Product) {
    const price = this.unitService.getUnitById(product.price.unit);
    const mass = this.unitService.getUnitById(product.quantity.unit);

    if (!price || !mass) {
      return null;
    }

    return price.translationId + '/' + mass.translationId;
  }

  getGeneratedProductPricingArray() {
    const result = [];
    for (const product of this.generatedProducts) {
      if (product.priceType && product.priceType === ProductFeeType.FREE) {
        result.push(this.translationService.get('FEE_TYPE.free'));
      } else if (product.price.unit && Utils.isNumeric(product.price.value) && product.quantity.unit && Utils.isNumeric(product.quantity.value)) {
        const prefix = product.type === ProductType.ThinDigestate ? this.translationService.get('FEE_TYPE.' + product.priceType) + ': ' : '';
        result.push(prefix + this.getNormalizedPriceProduct(product) + ' ' + this.getNormalizedUnitProduct(product));
      } else {
        result.push(null);
      }
    }

    return result;
  }

  onEditGeneratedProduct(index: number) {
    const dialogRef = this.dialog.open(EditGeneratedProductFinancialDialogComponent, {
      width: '900px',
      height: '625px',
      disableClose: true,
      data: {
        product: Product.deepCopy(this.generatedProducts[index]),
      },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((product: Product) => {
        if (product) {
          this.projectCommService.saveGeneratedProduct(product, index);
          this.generatedProducts = this.projectCommService.getGeneratedProductItems();
          this.pageDirty.emit(true);
        }
      });
  }

  // Capex Financials

  onAddCapexClick() {
    const dialogRef = this.dialog.open(AddCapexDialogComponent, {
      width: '900px',
      height: '600px',
      disableClose: true,
      data: {
        isEdit: false,
      },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((capex: FinancialCapex) => {
        if (capex) {
          this.capexService.updateTotalCapexSavedUnit(capex.cost.unit);
          this.capexService.addCapex(capex);
          this.capexService.saveCapex();
          this.pageDirty.emit(true);
        }
      });
  }

  getCapexPricingArray() {
    const result = [];
    for (const capex of this.capex) {
      if (capex.cost.unit && Utils.isNumeric(capex.cost.value)) {
        const value = Utils.roundToPrecision(capex.cost.value, 2);
        result.push(Utils.addCommas(value) + ' ' + this.unitService.getUnitById(capex.cost.unit).translationId);
      } else {
        result.push(null);
      }
    }

    return result;
  }

  onEditCapex(index: number) {
    const dialogRef = this.dialog.open(AddCapexDialogComponent, {
      width: '900px',
      height: '600px',
      disableClose: true,
      data: {
        isEdit: true,
        capex: FinancialCapex.deepCopy(this.capex[index]),
      },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((capex: FinancialCapex) => {
        if (capex) {
          this.capexService.updateTotalCapexSavedUnit(capex.cost.unit);
          this.capexService.updateCapexItem(capex, index);
          this.capexService.saveCapex();
          this.pageDirty.emit(true);
        }
      });
  }

  onRemoveCapex(index: number) {
    this.capexService.removeCapex(index);
    this.capexService.saveCapex();
    this.pageDirty.emit(true);
  }

  onEstimateCapexClick() {
    this.showCapexEstimationConfirmation = true;
  }

  onTotalCapexChanged() {
    if (this.totalCapex && this.totalCapex.unit) {
      if (this.totalCapex.value) {
        this.totalCapex.value = Utils.roundToPrecision(this.totalCapex.value, 2);
      }
      this.capexService.updateTotalCapexSavedUnit(this.totalCapex.unit);
      this.capexService.saveCapex();
      this.pageDirty.emit(true);
    }
  }

  performCapexEstimation() {
    // Save the project before performing estimation
    this.projectCommService.enableProjectSaveToDatabase();
    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(saved => {
        if (saved) {
          this.projectCommService.disableProjectSaveToDatabase();
          this.capexService.estimateCapex();
        }
      });
    this.projectCommService.saveProject();
    this.showCapexEstimationConfirmation = false;
    this.pageDirty.emit(true);
  }

  clearAllCapex() {
    const message = this.translationService.get('PROJECT.FINANCIAL_CAPEX.DELETE_ALL_CAPEX');
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: message },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(status => {
        if (status) {
          this.capexService.clearAll();
          this.capexService.saveCapex();
          this.pageDirty.emit(true);
        }
      });
  }

  // Opex Financials

  onAddOpexClick() {
    const dialogRef = this.dialog.open(AddOpexDialogComponent, {
      width: '900px',
      height: '600px',
      disableClose: true,
      data: {
        isEdit: false,
      },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((opex: FinancialOpex) => {
        if (opex) {
          this.opexService.updateTotalOpexSavedUnit(opex.cost.unit);
          this.opexService.addOpex(opex);
          this.opexService.saveOpex();
          this.pageDirty.emit(true);
        }
      });
  }

  /**
   * Get the price of the provided opex item  per year.
   * @param opex is the opex item for which the price will be returned.
   */
  getNormalizedPriceOpex(opex: FinancialOpex) {
    const cost = opex.cost;
    const period = opex.period;

    const periodInDays = this.unitService.getValueInBaseUnit(period);
    const value = Utils.roundToPrecision((cost.value / (periodInDays / 365)), 2).toString();

    // return cost per year in target unit
    return Utils.addCommas(value);
  }

  /**
   * Get the unit for the provided opex item.
   * @param opex is the opex item for which the price will be returned.
   */
  getNormalizedUnitOpex(opex: FinancialOpex) {
    const cost = this.unitService.getUnitById(opex.cost.unit);
    return (cost) ? cost.translationId + '/' + 'RESULTS.GENERAL.YEAR' : null;
  }

  /**
   * Get an array of normalized price data for all current opex items.
   */
  getOpexPricingArray() {
    const result = [];
    for (const opex of this.opex) {
      if (opex.cost.unit && Utils.isNumeric(opex.cost.value) && opex.period.unit && opex.period.value) {
        result.push(this.getNormalizedPriceOpex(opex) + ' ' + this.getNormalizedUnitOpex(opex));
      }  else {
        result.push(null);
      }
    }

    return result;
  }

  /**
   * Get a string that contains the opex percent of total capex. If the percentage isn't able to be returned a null
   * will be returned.
   */
  getOpexPercentageString() {
    if (this.opexPercentage) {
      return '(' + Utils.roundToPrecision(this.opexPercentage, 2) + this.translationService.get('PROJECT.FINANCIAL_OPEX.TOTAL_CAPEX') + ')';
    }
  }

  onEditOpex(index: number) {
    const dialogRef = this.dialog.open(AddOpexDialogComponent, {
      width: '900px',
      height: '600px',
      disableClose: true,
      data: {
        isEdit: true,
        opex: FinancialOpex.deepCopy(this.opexService.getOpexItemByIndex(index)),
      },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((opex: FinancialOpex) => {
        if (opex) {
          this.opexService.updateTotalOpexSavedUnit(opex.cost.unit);
          this.opexService.updateOpexItem(opex, index);
          this.opexService.saveOpex();
          this.pageDirty.emit(true);
        }
      });
  }

  onRemoveOpex(index: number) {
    this.opexService.removeOpex(index);
    this.opexService.saveOpex();
    this.pageDirty.emit(true);
  }

  onEstimateOpexClick() {
    this.showOpexEstimationConfirmation = true;
  }

  onTotalOpexChanged() {
    if (this.totalOpex && this.totalOpex.unit) {
      if (this.totalOpex.value) {
        this.totalOpex.value = Utils.roundToPrecision(this.totalOpex.value, 2);
      }
      this.opexService.updateTotalOpexSavedUnit(this.totalOpex.unit);
      this.opexService.saveOpex();
      this.pageDirty.emit(true);
    }
  }

  performOpexEstimation(opexPercentage: UnitValue) {
    // Save the project before performing estimation
    this.projectCommService.enableProjectSaveToDatabase();
    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(saved => {
        if (saved) {
          this.projectCommService.disableProjectSaveToDatabase();
          this.opexService.estimateOpex(opexPercentage);
        }
      });
    this.projectCommService.saveProject();
    this.showOpexEstimationConfirmation = false;
    this.pageDirty.emit(true);
  }

  clearAllOpex() {
    const message = this.translationService.get('PROJECT.FINANCIAL_OPEX.DELETE_ALL_OPEX');
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {message: message}
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(status => {
      if (status) {
        this.opexService.clearAll();
        this.opexService.saveOpex();
        this.pageDirty.emit(true);
      }
    });
  }
}
